import {
    ChangeDetectionStrategy,
    Component,
    InjectionToken,
    computed,
    effect,
    inject,
    signal,
} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {DateTime} from 'luxon';
import {catchError, map, of} from 'rxjs';
import {
    TRANSLATE,
    TranslatePipe,
    caseInsensitiveComparator,
    includesIgnoreCase,
    keyComparator,
} from '@brunata-angular/util';
import {
    DateTimePipe,
    FormatBytesPipe,
    InputDateGroupComponent,
    InputDropdownCheckboxesComponent,
    NumberPipe,
    SelectableItem,
    Sorting,
} from '@brunata-angular/ui';
import {
    ExportTableColumn,
    ExportTableComponent,
} from '../../shared/ui/export-table/export-table.component';
import {SavedExportsService} from '../saved-exports.service';
import {SavedExport} from '../saved-export.model';
import {
    SavedExportFilterOption,
    selectedOptionIds,
    toSelectableItems,
} from '../saved-exports-filters.model';
import {
    DeliveryLogEntry,
    deliveryFailureReasonLabelKeys,
    deliveryFailureReasonShortLabelKeys,
    deliveryFailureReasons,
    deliveryOutcomeLabelKeys,
    deliveryOutcomes,
} from '../delivery-log.model';
import {DeliveryOutcomeIconComponent} from '../delivery-outcome-icon/delivery-outcome-icon.component';

export type DeliveryLogModalInput = {
    readonly savedExport: SavedExport | null;
};

const defaultSorting: Sorting = {column: 'deliveredAt', direction: 'desc'};

@Component({
    selector: 'app-delivery-log-modal',
    imports: [
        DateTimePipe,
        DeliveryOutcomeIconComponent,
        FormatBytesPipe,
        NumberPipe,
        ReactiveFormsModule,
        TranslatePipe,
        ExportTableComponent,
        InputDateGroupComponent,
        InputDropdownCheckboxesComponent,
    ],
    templateUrl: './delivery-log-modal.component.html',
    styles: `
        .modal-body {
            height: 70vh;
        }

        .delivery-log-search {
            min-width: 12.5rem;
        }

        .delivery-log-date {
            min-width: 11rem;
        }

        .delivery-log-truncate {
            max-width: 14rem;
        }

        .delivery-log-reason {
            max-width: 0;
            width: 100%;
        }

        .delivery-log-expanded > td {
            --bs-table-bg-state: transparent;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryLogModalComponent {
    static readonly inputToken = new InjectionToken<DeliveryLogModalInput>(
        'DeliveryLogModalComponent.input',
    );

    protected readonly activeModal = inject(NgbActiveModal);

    private readonly savedExportsService = inject(SavedExportsService);
    private readonly translate = inject(TRANSLATE);
    private readonly input = inject(DeliveryLogModalComponent.inputToken);

    protected readonly savedExport = this.input.savedExport;

    private readonly statusOptions: SavedExportFilterOption[] = deliveryOutcomes.map(outcome => ({
        id: outcome,
        label: this.translate(deliveryOutcomeLabelKeys[outcome], 'online', 'export'),
    }));

    private readonly reasonOptions = computed<SavedExportFilterOption[]>(() => {
        const presentReasons = new Set(
            (this.page()?.deliveries ?? []).map(delivery => delivery.failureReason),
        );
        return deliveryFailureReasons
            .filter(reason => presentReasons.has(reason))
            .map(reason => ({
                id: reason,
                label: this.translate(
                    deliveryFailureReasonShortLabelKeys[reason],
                    'online',
                    'export',
                ),
            }));
    });

    // The dropdowns are value accessors over their own options, so the controls are seeded with them.
    protected readonly filters = new FormGroup({
        statuses: new FormControl<SelectableItem[]>(toSelectableItems(this.statusOptions, []), {
            nonNullable: true,
        }),
        deliveredOn: new FormControl<DateTime | null>(null),
        exportName: new FormControl('', {nonNullable: true}),
        destination: new FormControl('', {nonNullable: true}),
        reasons: new FormControl<SelectableItem[]>([], {nonNullable: true}),
    });

    protected readonly sorting = signal(defaultSorting);

    protected readonly columns: ExportTableColumn[] = [
        {
            label: this.translate('savedexport_deliverylog_column_status', 'online', 'export'),
            width: '15%',
        },
        {
            label: this.translate('savedexport_deliverylog_column_date', 'online', 'export'),
            sortColumn: 'deliveredAt',
            width: '19%',
        },
        {
            label: this.translate('savedexport_deliverylog_column_export', 'online', 'export'),
            sortColumn: 'exportName',
            width: '21%',
        },
        {
            label: this.translate('savedexport_deliverylog_column_destination', 'online', 'export'),
            width: '21%',
        },
        {
            label: this.translate('savedexport_deliverylog_column_reason', 'online', 'export'),
            width: '19%',
        },
        {label: '', width: '5%'},
    ];

    private readonly page = toSignal(
        this.savedExportsService
            .getDeliveries(this.input.savedExport?.savedExportId ?? null)
            .pipe(catchError(() => of(null))),
    );

    private readonly filterValues = toSignal(
        this.filters.valueChanges.pipe(map(() => this.filters.getRawValue())),
        {initialValue: this.filters.getRawValue()},
    );

    protected readonly isLoading = computed(() => this.page() === undefined);

    protected readonly hasFailed = computed(() => this.page() === null);

    private readonly deliveries = computed(() => {
        const deliveries = this.page()?.deliveries ?? [];
        const {deliveredOn, exportName, destination, statuses, reasons} = this.filterValues();
        const outcomes = selectedOptionIds(this.statusOptions, statuses);
        const failureReasons = selectedOptionIds(this.reasonOptions(), reasons);
        const matching = deliveries.filter(
            delivery =>
                (outcomes.length === 0 || outcomes.includes(delivery.outcome)) &&
                (deliveredOn === null ||
                    !deliveredOn.isValid ||
                    delivery.deliveredAt.toISODate() === deliveredOn.toISODate()) &&
                includesIgnoreCase(delivery.exportName, exportName) &&
                includesIgnoreCase(delivery.destination, destination) &&
                (failureReasons.length === 0 ||
                    (delivery.failureReason !== null &&
                        failureReasons.includes(delivery.failureReason))),
        );
        return this.sorted(matching);
    });

    /** More deliveries exist than one page carries, so the log says so rather than implying it is all. */
    protected readonly loadedCount = computed(() => this.page()?.deliveries.length ?? 0);

    protected readonly totalCount = computed(() => this.page()?.totalCount ?? 0);

    protected readonly rows = computed(() =>
        this.deliveries().map(delivery => ({
            delivery,
            key: `${delivery.savedExportId}/${delivery.scheduledFor}`,
            nextAttemptLabelKey: this.nextAttemptLabelKey(delivery),
            attemptsSummaryKey:
                delivery.attempts === 2
                    ? 'savedexport_deliverylog_details_attempts_first_failed_one'
                    : 'savedexport_deliverylog_details_attempts_first_failed_many',
            attemptsSummaryParams: [
                delivery.attempts.toString(),
                (delivery.attempts - 1).toString(),
            ],
        })),
    );

    protected readonly expandedKey = signal<string | null>(null);

    protected readonly failureReasonShortLabelKeys = deliveryFailureReasonShortLabelKeys;

    protected readonly failureReasonLabelKeys = deliveryFailureReasonLabelKeys;

    protected readonly outcomeLabelKeys = deliveryOutcomeLabelKeys;

    constructor() {
        effect(() => {
            this.filters.controls.reasons.setValue(toSelectableItems(this.reasonOptions(), []));
        });
    }

    onSort(sorting: Sorting): void {
        this.sorting.set(sorting);
    }

    resetFilters(): void {
        this.filters.setValue({
            statuses: toSelectableItems(this.statusOptions, []),
            deliveredOn: null,
            exportName: '',
            destination: '',
            reasons: toSelectableItems(this.reasonOptions(), []),
        });
        this.sorting.set(defaultSorting);
    }

    toggleDetails(key: string): void {
        this.expandedKey.update(expanded => (expanded === key ? null : key));
    }

    /**
     * A failed delivery is retried every hour for the rest of its scheduled day, while an hourly
     * schedule waits for its next hour and Export now is not retried. An hourly slot carries a
     * time of day, a daily slot only a date.
     */
    private nextAttemptLabelKey(delivery: DeliveryLogEntry): string | null {
        const scheduledFor = DateTime.fromISO(delivery.scheduledFor);
        if (
            delivery.outcome === 'DELIVERED' ||
            delivery.fromExportNow ||
            !scheduledFor.isValid ||
            !scheduledFor.hasSame(DateTime.now(), 'day')
        ) {
            return null;
        }
        return delivery.scheduledFor.includes('T')
            ? 'savedexport_deliverylog_next_attempt_next_hour'
            : 'savedexport_deliverylog_next_attempt_hourly';
    }

    private sorted(deliveries: DeliveryLogEntry[]): DeliveryLogEntry[] {
        const {column, direction} = this.sorting();
        const comparator =
            column === 'exportName'
                ? caseInsensitiveComparator<DeliveryLogEntry>(
                      delivery => delivery.exportName ?? '',
                      direction,
                  )
                : keyComparator<DeliveryLogEntry, number>(
                      delivery => delivery.deliveredAt.toMillis(),
                      direction,
                  );
        return [...deliveries].sort(comparator);
    }
}
